import { useMemo } from 'react';
import { PaymentsConfig1 } from '@frontend/api-payments-config';

export type MaxSurchargeInfoRecord = {
  maximumSurchargePercentage: number;
  state: string | undefined;
  locationId: string | undefined;
};

export const SURCHARGE_MIN_VALUE = 100;
const SURCHARGE_MAX_VALUE = 300;

export const useLocationCanSurcharge = (locationIds: string[]) => {
  const { data, isLoading } = PaymentsConfig1.Queries.useGetLocationCapabilitiesQuery({
    request: {
      locationIds: locationIds,
    },
    options: {
      enabled: locationIds.length > 0,
    },
  });

  const { enabledLocations, combinedMaxSurchargeInfo } = useMemo(() => {
    const enabledLocationsInfo = data?.capabilities?.filter((capability) => capability.supportsSurcharging) ?? [];
    const enabledLocations = enabledLocationsInfo.map((capability) => capability.locationId) || [];

    const firstLocationInfo = enabledLocationsInfo[0];
    const defaultMaxSurchargeInfo: MaxSurchargeInfoRecord = {
      maximumSurchargePercentage: firstLocationInfo?.maximumSurchargePercentage ?? SURCHARGE_MAX_VALUE,
      state: firstLocationInfo?.state,
      locationId: firstLocationInfo?.locationId,
    };

    // When multiple locations are selected, we need to find the least allowable surcharge percentage among them
    const combinedMaxSurchargeInfo: MaxSurchargeInfoRecord =
      enabledLocationsInfo.reduce((min, info) => {
        const { maximumSurchargePercentage: maxPercent, state, locationId } = info;
        const { maximumSurchargePercentage: combinedPercent, state: commonState } = min;
        if (maxPercent && (!combinedPercent || maxPercent < combinedPercent)) {
          min.maximumSurchargePercentage = maxPercent;
          min.state = commonState === state ? state : undefined;
          min.locationId = locationId;
        }
        return min;
      }, defaultMaxSurchargeInfo as MaxSurchargeInfoRecord) || defaultMaxSurchargeInfo;

    return { enabledLocations, combinedMaxSurchargeInfo };
  }, [data?.capabilities]);

  return {
    isLoading,
    enabledLocations,
    canSurcharge: enabledLocations.length > 0 ? true : false,
    combinedMaxSurchargeInfo,
  };
};
